import { defineStore } from "pinia";
import { firstValueFrom, Observable } from "rxjs";
import {
  logout,
  verifyToken,
} from "@ntprsrf-frontend/service/src/services/intranet/auth.service";
import { getSftpAccountExists } from "@ntprsrf-frontend/service/src/services/internet/sftpConnection.service";
import { PortalLoginResponse } from "@ntprsrf-frontend/service/src/responses/intranet/auth.response";
import {
  eGuaranteeTypes,
  CompanyTypes,
} from "@ntprsrf-frontend/internet/src/constants/APIEnums";
import type { Router } from "vue-router";
import { INTERNET_LOGIN_URL } from "@ntprsrf-frontend/common/src/constants/login.constants";
import { redirectAfterCorpass } from "@ntprsrf-frontend/internet/src/utils/governmentServices";
import { v4 as uuidv4 } from 'uuid';
import { performAudit } from "@ntprsrf-frontend/internet/src/utils/pageAudit";
import { triggerAllPageExits } from "@ntprsrf-frontend/internet/src/utils/useUserInteraction";
import { confirmLogout } from "@ntprsrf-frontend/internet/src/utils/useLogoutGuard";
import { useELOAStore } from "./eloa.store";
declare module "pinia" {
  export interface PiniaCustomProperties {
    $router: Router;
  }
}
interface AuthState {
  userId: string;
  username: string;
  companyId: string;
  companyName: string;
  companyUEN: string;
  companyType: string;
  eGuaranteeType: string;
  eGuaranteePrivilege: boolean;
  roleId: string;
  roleType: string;
  groupType: string
  isFirstLogin: boolean;
  hasAgreedToTNC?: boolean;
  loginKey: string;
  timeoutPageAccessible: boolean;
  hasSftpAccount?: boolean;
}

export default defineStore("auth", {
  persist: true,
  state: (): AuthState => ({
    userId: "",
    username: "",
    companyId: "",
    companyName: "",
    companyUEN: "",
    companyType: CompanyTypes.DEFAULT.VALUE,
    eGuaranteeType: "",
    eGuaranteePrivilege: true,
    roleId: "",
    roleType: "",
    groupType: "",
    isFirstLogin: false,
    hasAgreedToTNC: false,
    loginKey: "",
    timeoutPageAccessible: false,
    hasSftpAccount: undefined,
  }),
  getters: {
    isLogin: (state) => !!state.userId,
    isVASProvider: (state) =>
      state.companyType === CompanyTypes.VAS_PROVIDER.VALUE,
    isNormalEGuaranteeType(state) {
      return state.eGuaranteeType == eGuaranteeTypes.NORMAL.VALUE;
    },
    isGovernmentAgency(state) {
      return state.eGuaranteeType == eGuaranteeTypes.GOVERNMENT_AGENCY.VALUE;
    },
    isFinancialInstitution(state) {
      return (
        state.eGuaranteeType == eGuaranteeTypes.FINANCIAL_INSTITUTION.VALUE
      );
    },
    getUserEguaranteeType(state) {
      return state.eGuaranteeType;
    },
  },
  actions: {
    setUserInfo(loginResponse: PortalLoginResponse) {
      this.userId = loginResponse.data.user.userId;
      this.username = loginResponse.data.user.username;
      this.companyId = loginResponse.data.company.companyId;
      this.companyName = loginResponse.data.company.companyName;
      this.companyUEN = loginResponse.data.company.companyUEN;
      this.companyType = loginResponse.data.company.companyType ?? CompanyTypes.DEFAULT.VALUE;
      this.eGuaranteeType = loginResponse.data.company.eGuaranteeType;
      this.eGuaranteePrivilege = loginResponse.data.company.eGuaranteePrivilege;
      this.roleId = loginResponse.data.user.roles[0].roleId;
      this.roleType = loginResponse.data.user.roles[0].roleType;
      this.groupType = loginResponse.data.user.groupType;
      this.isFirstLogin = loginResponse.data.isFirstLogin;
      this.loginKey = uuidv4();

      this.refreshSftpAccountExists();
    },
    setTNCAgreement(hasAgreed: boolean) {
      this.hasAgreedToTNC = hasAgreed;
    },
    setTimeoutPageAccessible(val: boolean) {
      this.timeoutPageAccessible = val;
    },
    async login(obs: Observable<PortalLoginResponse>) {
      const response = await firstValueFrom(obs);
      this.setUserInfo(response);
      if (this.isFirstLogin) {
        await this.$router.push({
          name: "TERMS_AND_CONDITIONS"
        });
        return;
      }
      redirectAfterCorpass(
        {
          isLogin: this.isLogin,
          isFirstLogin: this.isFirstLogin
        },
        this.$router
      )
    },
    async signOut(gotoTimeout: boolean = false) {
      // Session timeouts sign out unconditionally; guards still run so pages
      // can silently clean up (e.g. save a draft) without prompting the user.
      if (gotoTimeout) {
        await confirmLogout(true);
      } else if (!(await confirmLogout())) { // User initiated logouts let pages confirm first (e.g. unsaved changes).
        return;
      }
      try {
        await triggerAllPageExits();
        await performAudit(this.$router.currentRoute.value);
        await firstValueFrom(logout());
      } finally {
        const eloaStore = useELOAStore();

        if (gotoTimeout) {
          await this.resetStoreThenGoToTimeoutPage();
        } else {
          await this.resetStoreThenGoToHome();
        }
        localStorage.removeItem('serviceDetailId');
        localStorage.removeItem("redirect");

        eloaStore.setFetchId(null);
        eloaStore.setInitialLoad(true);
        eloaStore.unsubscribeFetchingSubscription();
      }
    },
    async refreshSftpAccountExists(onLoadingPipeline?: (obs: Observable<any>) => Observable<any>) {
      try {
        const pipeline = onLoadingPipeline ?? ((obs) => obs);
        const res = await firstValueFrom(getSftpAccountExists().pipe(pipeline));
        this.hasSftpAccount = res.data;
      } catch (error) {
        console.error("Failed to refresh SFTP account existence", error);
        // Keep the previous state when API request fails.
      }
      return this.hasSftpAccount;
    },
    async resetStoreThenGoToTimeoutPage() {
      this.$reset();
      this.setTimeoutPageAccessible(true)
      await this.$router.replace({
        name: "TIMEOUT",
      });
    },
    async resetStoreThenGoToHome() {
      this.$reset();
      if (INTERNET_LOGIN_URL) {
        window.location.href = INTERNET_LOGIN_URL;
        return;
      }
      await this.$router.push({
        name: "HOME PAGE",
      });
    },
    async callVerifyToken() {
      const response = await firstValueFrom(verifyToken());
      if (
        this.userId !== response.data.user.userId ||
        this.companyId !== response.data.company.companyId
      ) {
        this.userId = response.data.user.userId;
        this.username = response.data.user.username;
        this.companyId = response.data.company.companyId;
        this.companyName = response.data.company.companyName;
        this.companyUEN = response.data.company.companyUEN;
        this.companyType = response.data.company.companyType ?? CompanyTypes.DEFAULT.VALUE;
        this.eGuaranteeType = response.data.company.eGuaranteeType;
        this.eGuaranteePrivilege = response.data.company.eGuaranteePrivilege;
        this.isFirstLogin = response.data.isFirstLogin;
      }
    },
  },
});
